import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getUser, getUserActivity, updateUser, creditUserWallet, resolveMediaUrl,
  getAdminAppointments,
} from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, EmptyState, TableSkeleton,
  AstrologerLink,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import {
  formatCurrency, formatDate, formatDateTime, formatDuration,
} from '../../utils/format';
import {
  ArrowLeft, Ban, CheckCircle, Clock, DollarSign, MessageSquare, PhoneCall,
  Video, RefreshCw, Star, User, Users, Wallet, MapPin, Calendar,
  Mail, Phone, Globe, Activity,
} from 'lucide-react';
import toast from 'react-hot-toast';

type TabKey = 'overview' | 'profile' | 'consultations' | 'wallet' | 'transactions' | 'appointments' | 'reviews';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'overview', label: 'Overview', icon: <Activity size={14} /> },
  { key: 'profile', label: 'Profile', icon: <User size={14} /> },
  { key: 'consultations', label: 'Consultations', icon: <MessageSquare size={14} /> },
  { key: 'appointments', label: 'Appointments', icon: <Calendar size={14} /> },
  { key: 'wallet', label: 'Wallet', icon: <Wallet size={14} /> },
  { key: 'transactions', label: 'Transactions', icon: <DollarSign size={14} /> },
  { key: 'reviews', label: 'Reviews', icon: <Star size={14} /> },
];

const TYPE_ICON: Record<string, React.ReactNode> = {
  chat: <MessageSquare size={13} className="text-blue-600" />,
  call: <PhoneCall size={13} className="text-green-600" />,
  video: <Video size={13} className="text-purple-600" />,
};

function Avatar({ name, image, size = 64 }: { name?: string; image?: string; size?: number }) {
  const src = resolveMediaUrl(image);
  if (src) {
    return (
      <img
        src={src}
        alt={name || 'User'}
        className="rounded-full object-cover border-2 border-white shadow"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold shadow"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {(name?.[0] || '?').toUpperCase()}
    </div>
  );
}

function StatTile({
  label, value, icon, color = 'text-gray-900', sub,
}: {
  label: string; value: React.ReactNode; icon: React.ReactNode; color?: string; sub?: string;
}) {
  return (
    <div className="card p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className={`text-lg font-bold mt-0.5 truncate ${color}`}>{value}</p>
        {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</p>
      <p className="text-sm font-semibold text-gray-900 mt-1 break-words">{value || '—'}</p>
    </div>
  );
}

export default function UserDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [tab, setTab] = useState<TabKey>('overview');
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('Manual admin credit');

  const { data: detail, isLoading, refetch } = useQuery({
    queryKey: ['admin-user-detail', id],
    queryFn: async () => {
      const r = await getUser(id!);
      return r.data.data;
    },
    enabled: !!id,
  });

  const listTab =
    tab === 'consultations' || tab === 'reviews'
      ? tab
      : tab === 'wallet' || tab === 'transactions'
        ? 'transactions'
        : null;

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin-user-activity', id, listTab, page, typeFilter, statusFilter],
    queryFn: async () => {
      const params: any = { tab: listTab, page, limit: 15 };
      if (listTab === 'consultations') {
        if (typeFilter) params.type = typeFilter;
        if (statusFilter) params.status = statusFilter;
      }
      const r = await getUserActivity(id!, params);
      return r.data;
    },
    enabled: !!id && !!listTab,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: any) => updateUser(id!, payload),
    onSuccess: () => {
      toast.success('User updated');
      qc.invalidateQueries({ queryKey: ['admin-user-detail', id] });
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: () => toast.error('Update failed'),
  });

  const creditMutation = useMutation({
    mutationFn: () => creditUserWallet(id!, Math.round(Number(creditAmount) * 100), creditReason),
    onSuccess: () => {
      toast.success('Wallet credited');
      setCreditOpen(false);
      setCreditAmount('');
      qc.invalidateQueries({ queryKey: ['admin-user-detail', id] });
      qc.invalidateQueries({ queryKey: ['admin-user-activity', id] });
    },
    onError: () => toast.error('Credit failed'),
  });

  const user = detail?.user;
  const stats = detail?.stats || {};
  const list = listData?.data || [];
  const pagination = listData?.pagination;

  const preferences = useMemo(() => {
    const prefs = user?.preferences;
    return Array.isArray(prefs) ? prefs : [];
  }, [user]);

  if (!id) {
    return <div className="p-6"><EmptyState message="User not found" /></div>;
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <button
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back to Users
        </button>
      </div>

      <PageHeader
        title={user?.name || user?.phone || 'User Details'}
        subtitle="Complete profile, consultations, wallet & activity"
      >
        <button onClick={() => refetch()} className="btn-secondary btn-sm">
          <RefreshCw size={14} /> Refresh
        </button>
        <Link to={`/ai-chat?userId=${id}`} className="btn-secondary btn-sm inline-flex items-center gap-1">
          <MessageSquare size={14} /> AI sessions
        </Link>
        <button onClick={() => setCreditOpen(true)} className="btn-secondary btn-sm">
          <Wallet size={14} /> Credit Wallet
        </button>
        {user && (
          <button
            onClick={() => updateMutation.mutate({ isBlocked: !user.isBlocked })}
            className={`btn-sm ${user.isBlocked ? 'btn-primary' : 'btn-danger'}`}
          >
            {user.isBlocked ? <><CheckCircle size={14} /> Unblock</> : <><Ban size={14} /> Block</>}
          </button>
        )}
      </PageHeader>

      {/* Hero card */}
      <div className="card p-5 mb-5">
        {isLoading ? (
          <TableSkeleton rows={2} cols={4} />
        ) : user ? (
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <div className="flex items-center gap-4 min-w-0">
              <Avatar name={user.name} image={user.profileImage} size={72} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-gray-900 truncate">{user.name || 'Unnamed User'}</h2>
                  <StatusBadge status={user.isBlocked ? 'blocked' : user.isActive ? 'active' : 'inactive'} />
                  <span className={user.isProfileComplete ? 'badge-green' : 'badge-yellow'}>
                    {user.isProfileComplete ? 'Profile Complete' : 'Incomplete'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1"><Phone size={13} /> {user.phone}</span>
                  {user.email && <span className="inline-flex items-center gap-1"><Mail size={13} /> {user.email}</span>}
                  <span className="inline-flex items-center gap-1"><Globe size={13} /> {(user.language || 'en').toUpperCase()}</span>
                  <span className="inline-flex items-center gap-1"><Calendar size={13} /> Joined {formatDate(user.createdAt)}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:ml-auto w-full lg:w-auto">
              <div className="bg-orange-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-orange-700 font-semibold">Wallet</p>
                <p className="text-base font-bold text-orange-800">{formatCurrency(stats.walletBalance || 0)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-blue-700 font-semibold">Sessions</p>
                <p className="text-base font-bold text-blue-800">{stats.consultationCount || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-purple-700 font-semibold">Talk Time</p>
                <p className="text-base font-bold text-purple-800">{formatDuration(stats.totalDurationSeconds || 0)}</p>
              </div>
              <div className="bg-green-50 rounded-xl px-3 py-2 text-center">
                <p className="text-[11px] text-green-700 font-semibold">Spent</p>
                <p className="text-base font-bold text-green-800">{formatCurrency(stats.totalSpent || 0)}</p>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState message="User not found" />
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-gray-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setPage(1); }}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-primary-500 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ───────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            <StatTile label="Chat Sessions" value={stats.chatCount || 0} icon={<MessageSquare size={16} />} color="text-blue-600" />
            <StatTile label="Audio Sessions" value={stats.callCount || 0} icon={<PhoneCall size={16} />} color="text-green-600" />
            <StatTile label="Video Sessions" value={stats.videoCount || 0} icon={<Video size={16} />} color="text-purple-600" />
            <StatTile label="Total Talk Time" value={formatDuration(stats.totalDurationSeconds || 0)} icon={<Clock size={16} />} color="text-orange-600" />
            <StatTile label="Messages Sent" value={stats.messageCount || 0} icon={<MessageSquare size={16} />} color="text-indigo-600" />
            <StatTile label="Astrologers Talked" value={stats.uniqueAstrologers || 0} icon={<Users size={16} />} color="text-teal-600" />
            <StatTile label="Consultation Spend" value={formatCurrency(stats.totalSpent || 0)} icon={<DollarSign size={16} />} color="text-emerald-600" />
            <StatTile label="Wallet" value={formatCurrency(stats.walletBalance || 0)} icon={<Wallet size={16} />} color="text-cyan-600" />
            <StatTile label="Reviews Written" value={stats.reviewCount || 0} icon={<Star size={16} />} color="text-amber-600" />
            <StatTile label="Completed" value={stats.endedCount || 0} icon={<CheckCircle size={16} />} color="text-green-700" />
            <StatTile label="Cancelled / Missed" value={stats.cancelledCount || 0} icon={<Ban size={16} />} color="text-red-600" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {/* Top astrologers */}
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Astrologers Consulted</h3>
                <span className="text-xs text-gray-500">{detail?.topAstrologers?.length || 0} shown</span>
              </div>
              {!detail?.topAstrologers?.length ? (
                <div className="p-6"><EmptyState message="No consultations yet" icon={<Users size={28} />} /></div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {detail.topAstrologers.map((row: any) => (
                    <div key={String(row._id)} className="px-4 py-3 flex items-center gap-3">
                      <Avatar name={row.astrologer?.name} image={row.astrologer?.profileImage} size={40} />
                      <div className="flex-1 min-w-0">
                        <AstrologerLink
                          entity={row.astrologer || { _id: row._id }}
                          className="text-sm font-semibold text-primary-600 hover:underline truncate block"
                        >
                          {row.astrologer?.name || 'Astrologer'}
                        </AstrologerLink>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {row.sessions} sessions · {formatDuration(row.duration || 0)} · Chat {row.chat} · Audio {row.call} · Video {row.video}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(row.spent || 0)}</p>
                        <p className="text-[11px] text-gray-400">{formatDate(row.lastSessionAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent consultations snapshot */}
            <div className="card overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Recent Consultations</h3>
                <button onClick={() => setTab('consultations')} className="text-xs text-primary-600 font-semibold hover:underline">View all</button>
              </div>
              {!detail?.recentConsultations?.length ? (
                <div className="p-6"><EmptyState message="No sessions yet" icon={<MessageSquare size={28} />} /></div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {detail.recentConsultations.map((c: any) => (
                    <div key={c._id} className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center">
                        {TYPE_ICON[c.type] || <MessageSquare size={13} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <AstrologerLink
                          entity={c.astrologerId}
                          className="text-sm font-semibold text-primary-600 hover:underline truncate block"
                        >
                          {c.astrologerId?.name || 'Astrologer'}
                        </AstrologerLink>
                        <p className="text-xs text-gray-500 capitalize">{c.type} · {formatDuration(c.duration || 0)}</p>
                      </div>
                      <StatusBadge status={c.status} />
                      <span className="text-sm font-semibold text-gray-800 w-20 text-right">{formatCurrency(c.totalAmount || 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Profile ────────────────────────────────────────────────────────── */}
      {tab === 'profile' && (
        <div className="card p-5">
          {isLoading ? <TableSkeleton rows={6} cols={3} /> : user ? (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Personal Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Full Name" value={user.name} />
                  <InfoCell label="Phone" value={user.phone} />
                  <InfoCell label="Email" value={user.email} />
                  <InfoCell label="Gender" value={user.gender ? String(user.gender).charAt(0).toUpperCase() + String(user.gender).slice(1) : '—'} />
                  <InfoCell label="Language" value={(user.language || 'en').toUpperCase()} />
                  <InfoCell label="Country Code" value={user.countryCode || '+91'} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Birth Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Date of Birth" value={user.dateOfBirth ? formatDate(user.dateOfBirth) : '—'} />
                  <InfoCell label="Time of Birth" value={user.timeOfBirth || '—'} />
                  <InfoCell
                    label="Place of Birth"
                    value={user.placeOfBirth ? (
                      <span className="inline-flex items-center gap-1"><MapPin size={12} /> {user.placeOfBirth}</span>
                    ) : '—'}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Interests</h3>
                {preferences.length === 0 ? (
                  <p className="text-sm text-gray-500">No preferences selected</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {preferences.map((p: string) => (
                      <span key={p} className="badge-orange capitalize">{p}</span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Account Status</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <InfoCell label="Active" value={user.isActive ? 'Yes' : 'No'} />
                  <InfoCell label="Blocked" value={user.isBlocked ? 'Yes' : 'No'} />
                  <InfoCell label="Blocked Reason" value={user.blockedReason || '—'} />
                  <InfoCell label="Profile Complete" value={user.isProfileComplete ? 'Yes' : 'No'} />
                  <InfoCell label="Joined" value={formatDateTime(user.createdAt)} />
                  <InfoCell label="Last Active" value={user.lastActiveAt ? formatDateTime(user.lastActiveAt) : '—'} />
                  <InfoCell label="User ID" value={<span className="font-mono text-xs">{user._id}</span>} />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* ── Consultations ──────────────────────────────────────────────────── */}
      {tab === 'consultations' && (
        <div className="space-y-4">
          <div className="card p-3 flex flex-wrap gap-2 items-center">
            {[['','All'], ['chat','Chat'], ['call','Audio'], ['video','Video']].map(([v, l]) => (
              <button
                key={v || 'all'}
                onClick={() => { setTypeFilter(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${typeFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {l}
              </button>
            ))}
            <div className="w-px h-5 bg-gray-200 mx-1" />
            {[['','All Status'], ['ended','Ended'], ['active','Active'], ['cancelled','Cancelled'], ['missed','Missed']].map(([v, l]) => (
              <button
                key={`s-${v || 'all'}`}
                onClick={() => { setStatusFilter(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${statusFilter === v ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="card overflow-hidden">
            {listLoading ? (
              <div className="p-5"><TableSkeleton rows={8} cols={7} /></div>
            ) : list.length === 0 ? (
              <EmptyState message="No consultations found" icon={<MessageSquare size={32} />} />
            ) : (
              <>
                <Table>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-th">#</th>
                      <th className="table-th">Astrologer</th>
                      <th className="table-th">Type</th>
                      <th className="table-th">Status</th>
                      <th className="table-th">Duration</th>
                      <th className="table-th">Messages</th>
                      <th className="table-th">Rate</th>
                      <th className="table-th">Amount</th>
                      <th className="table-th">Started</th>
                      <th className="table-th">Ended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c: any, idx: number) => (
                      <tr key={c._id} className="table-row">
                        <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                        <td className="table-td">
                          <div className="flex items-center gap-2">
                            <Avatar name={c.astrologerId?.name} image={c.astrologerId?.profileImage} size={32} />
                            <div className="min-w-0">
                              <AstrologerLink
                                entity={c.astrologerId}
                                className="text-xs font-semibold text-primary-600 hover:underline truncate block"
                              >
                                {c.astrologerId?.name || '—'}
                              </AstrologerLink>
                              <Link to={`/consultations/${c._id}`} className="text-[11px] text-primary-600 hover:underline">Open chat / details</Link>
                              {c.intakeData?.question && (
                                <p className="text-[11px] text-gray-400 truncate max-w-[180px]">{c.intakeData.question}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="table-td">
                          <span className="inline-flex items-center gap-1 capitalize text-xs font-medium">
                            {TYPE_ICON[c.type]} {c.type === 'call' ? 'Audio' : c.type}
                          </span>
                        </td>
                        <td className="table-td"><StatusBadge status={c.status} /></td>
                        <td className="table-td text-xs font-semibold">{formatDuration(c.duration || 0)}</td>
                        <td className="table-td text-xs">{c.type === 'chat' ? (c.messageCount ?? 0) : '—'}</td>
                        <td className="table-td text-xs">{formatCurrency(c.pricePerMinute || 0)}/min</td>
                        <td className="table-td text-xs font-bold">{formatCurrency(c.totalAmount || c.reservedAmount || 0)}</td>
                        <td className="table-td text-xs whitespace-nowrap">{c.startedAt ? formatDateTime(c.startedAt) : formatDateTime(c.createdAt)}</td>
                        <td className="table-td text-xs whitespace-nowrap">{c.endedAt ? formatDateTime(c.endedAt) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {pagination && pagination.pages > 1 && (
                  <div className="p-4 border-t border-gray-100">
                    <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Wallet ─────────────────────────────────────────────────────────── */}
      {tab === 'wallet' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile label="Current Balance" value={formatCurrency(stats.walletBalance || 0)} icon={<Wallet size={16} />} color="text-green-600" />
            <StatTile label="Consultation Spend" value={formatCurrency(stats.totalSpent || 0)} icon={<DollarSign size={16} />} color="text-orange-600" />
          </div>

          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Transaction History</h3>
            </div>
            {listLoading ? (
              <div className="p-5"><TableSkeleton rows={8} cols={5} /></div>
            ) : list.length === 0 ? (
              <EmptyState message="No wallet transactions" icon={<Wallet size={32} />} />
            ) : (
              <>
                <Table>
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="table-th">#</th>
                      <th className="table-th">Type</th>
                      <th className="table-th">Category</th>
                      <th className="table-th">Description</th>
                      <th className="table-th">Amount</th>
                      <th className="table-th">Balance After</th>
                      <th className="table-th">Status</th>
                      <th className="table-th">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((t: any, idx: number) => (
                      <tr key={t._id} className="table-row">
                        <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                        <td className="table-td">
                          <span className={`badge ${t.type === 'credit' ? 'badge-green' : 'badge-red'} capitalize`}>{t.type}</span>
                        </td>
                        <td className="table-td text-xs capitalize">{String(t.category || '').replace(/_/g, ' ')}</td>
                        <td className="table-td text-xs max-w-[220px] truncate">{t.description}</td>
                        <td className={`table-td text-xs font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount || 0)}
                        </td>
                        <td className="table-td text-xs">{formatCurrency(t.balanceAfter || 0)}</td>
                        <td className="table-td"><StatusBadge status={t.status || 'completed'} /></td>
                        <td className="table-td text-xs whitespace-nowrap">{formatDateTime(t.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {pagination && pagination.pages > 1 && (
                  <div className="p-4 border-t border-gray-100">
                    <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'appointments' && (
        <UserAppointmentsTab userId={id!} />
      )}

      {tab === 'transactions' && (
        <div className="card overflow-hidden">
          {listLoading ? (
            <div className="p-5"><TableSkeleton rows={8} cols={5} /></div>
          ) : list.length === 0 ? (
            <EmptyState message="No transactions" icon={<Wallet size={32} />} />
          ) : (
            <>
              <Table>
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="table-th">Type</th>
                    <th className="table-th">Category</th>
                    <th className="table-th">Amount</th>
                    <th className="table-th">Balance</th>
                    <th className="table-th">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((t: any) => (
                    <tr key={t._id} className="table-row">
                      <td className="table-td capitalize text-xs">{t.type}</td>
                      <td className="table-td text-xs">{t.category}</td>
                      <td className={`table-td text-xs font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(t.amount || 0)}
                      </td>
                      <td className="table-td text-xs">{formatCurrency(t.balanceAfter || 0)}</td>
                      <td className="table-td text-xs">{formatDateTime(t.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {pagination && pagination.pages > 1 && (
                <div className="p-4 border-t">
                  <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Reviews ────────────────────────────────────────────────────────── */}
      {tab === 'reviews' && (
        <div className="card overflow-hidden">
          {listLoading ? (
            <div className="p-5"><TableSkeleton rows={6} cols={4} /></div>
          ) : list.length === 0 ? (
            <EmptyState message="No reviews written by this user" icon={<Star size={32} />} />
          ) : (
            <>
              <Table>
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="table-th">#</th>
                    <th className="table-th">Astrologer</th>
                    <th className="table-th">Rating</th>
                    <th className="table-th">Comment</th>
                    <th className="table-th">Tags</th>
                    <th className="table-th">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r: any, idx: number) => (
                    <tr key={r._id} className="table-row">
                      <td className="table-td text-xs text-gray-400">{(page - 1) * 15 + idx + 1}</td>
                      <td className="table-td">
                        <div className="flex items-center gap-2">
                          <Avatar name={r.astrologerId?.name} image={r.astrologerId?.profileImage} size={28} />
                          <AstrologerLink
                            entity={r.astrologerId}
                            className="text-xs font-semibold text-primary-600 hover:underline"
                          >
                            {r.astrologerId?.name || '—'}
                          </AstrologerLink>
                        </div>
                      </td>
                      <td className="table-td">
                        <span className="inline-flex items-center gap-1 text-amber-600 font-bold text-xs">
                          <Star size={12} fill="currentColor" /> {r.rating}
                        </span>
                      </td>
                      <td className="table-td text-xs max-w-[280px]">{r.comment || '—'}</td>
                      <td className="table-td">
                        <div className="flex flex-wrap gap-1">
                          {(r.tags || []).map((tag: string) => (
                            <span key={tag} className="badge-gray text-[10px]">{tag}</span>
                          ))}
                        </div>
                      </td>
                      <td className="table-td text-xs whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {pagination && pagination.pages > 1 && (
                <div className="p-4 border-t border-gray-100">
                  <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={15} onPageChange={setPage} />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Modal open={creditOpen} onClose={() => setCreditOpen(false)} title={`Credit Wallet — ${user?.name || user?.phone}`} size="sm">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Amount (₹)</label>
            <input type="number" min="1" className="input" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="100" autoFocus />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
            <input className="input" value={creditReason} onChange={(e) => setCreditReason(e.target.value)} />
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setCreditOpen(false)} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button
              onClick={() => creditMutation.mutate()}
              disabled={!creditAmount || creditMutation.isPending}
              className="btn-primary flex-1 justify-center"
            >
              {creditMutation.isPending ? 'Crediting…' : 'Credit Wallet'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function UserAppointmentsTab({ userId }: { userId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-user-appointments', userId],
    queryFn: () => getAdminAppointments({ userId, limit: 20 }).then((r) => r.data.data),
  });
  const rows = data?.data || [];
  return (
    <div className="card overflow-hidden">
      {isLoading ? (
        <div className="p-5"><TableSkeleton rows={5} cols={4} /></div>
      ) : rows.length === 0 ? (
        <EmptyState message="No appointments" icon={<Calendar size={32} />} />
      ) : (
        <Table>
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="table-th">Astrologer</th>
              <th className="table-th">Type</th>
              <th className="table-th">Scheduled</th>
              <th className="table-th">Status</th>
              <th className="table-th">Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a: any) => (
              <tr key={a._id} className="table-row">
                <td className="table-td"><AstrologerLink entity={a.astrologerId}>{a.astrologerId?.name || '—'}</AstrologerLink></td>
                <td className="table-td text-xs capitalize">{a.type === 'call' ? 'Audio' : a.type}</td>
                <td className="table-td text-xs">{formatDateTime(a.scheduledAt)}</td>
                <td className="table-td"><StatusBadge status={a.status} /></td>
                <td className="table-td text-xs">{formatCurrency(a.amount || a.totalAmount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
